import {useEffect, useRef, useState} from "react";
import {dataFetch, getItem, getUrl} from "./lib";
import {identify, setStep, setTrack, setTrackStep} from "./tracking";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import emailDomains from "assets/domains.json";
import Cookies from "js-cookie";
import {useSurvey} from "./useSurvey";

export const useEmail = ({step, toAdd, nextUrl, subscribe, onSubmit} = {}) => {

  const
    {t, i18n} = useTranslation('common', {keyPrefix: 'email'}),
    // {t: t0} = useTranslation(),
    navigate = useNavigate()

  const {
    surveyList,
    state: {resultList},
    getResults
  } = useSurvey()

  const
    inputRef = useRef(null),

    [isLoading, setIsLoading] = useState(false),
    [validated, setValidated] = useState(false),
    [email, setEmail] = useState(''),
    [suggestions, setSuggestions] = useState([])

  // console.log('results', getResults())

  useEffect(() => {
    if (!step)
      return

    if (resultList.length < surveyList.length) {
      navigate(getUrl('/'), {replace: true})
      return
    }

    // if (!window.localStorage.getItem('program_results')) {
    //   navigate('/loading', {replace: true})
    //   return
    // }

    const styles = document.createElement("style")
    styles.innerHTML =
      `.suggestions { position: absolute; z-index: 1; list-style: none; height: auto; max-height: 242px; overflow-y: auto; margin: -6px 0 0 0; padding: 0 }
.suggestions li { padding: 0.5rem 1.25rem; cursor: pointer; margin: 0 }
.suggestions li:hover { color: var(--white-100); background-color: var(--bg-100) }`
    document.head.appendChild(styles)

    setStep(step, toAdd)
  }, [])

  useEffect(() => {
    if (!step)
      return

    const handleKeyDown = (e) => {
      if (e.key === 'Escape')
        setSuggestions([])
    }

    const handleClickOutside = (e) => {
      if (
        suggestions.length > 0 &&
        !document.querySelector('.suggestions')?.contains(e.target)
      )
        setSuggestions([])
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('touchstart', handleClickOutside)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [suggestions])

  const validateEmail = (email) => {
    return !!String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      )
  }

  function handleChange(e) {
    const value = e.target.value

    setEmail(value)
    setValidated(false)

    const [localPart, domainPart] = value.split('@')

    if (domainPart !== undefined) {
      const suggestionList =
        domainPart.length >= 1
          ? emailDomains.filter((domain) => domain.startsWith(domainPart) && domain !== domainPart).map((domain) => `${localPart}@${domain}`)
          : ['gmail.com', 'yahoo.com', 'hotmail.com', 'aol.com', 'msn.com', 'proton.me'].map((domain) => `${localPart}@${domain}`)

      setSuggestions(suggestionList)
    } else
      setSuggestions([])
  }

  function handleSubmit(e, params = {}, callback) {
    if (e?.preventDefault)
      e.preventDefault()

    if (isLoading)
      return

    if (!validateEmail(params.email || email)) {
      alert(t('checkEmail'))
      return
    }

    if (email.endsWith('.con') && !validated) {
      setValidated(true)

      if (window.confirm(t('checkEmail2')))
        setEmail(email.replace(/\.con$/, '.com'))

      return
    }

    const getFbp = () => {
      let result = /_fbp=(fb\.1\.\d+\.\d+)/.exec(window.document.cookie)

      if (!(result && result[1]))
        return null

      return result[1]
    }

    const getFpc = () => {
      let result = /_fbc=(fb\.1\.\d+\.\S+)/.exec(window.document.cookie)

      if (!(result && result[1]))
        return null

      return result[1].replace(';', '')
    }

    setIsLoading(true)

    const data = {
      lang: i18n.resolvedLanguage,
      age: window.localStorage.getItem('age'),
      email,
      fbp: getFbp(),
      fbc: getFpc(),
      positionList: resultList,
      resultList: getResults(),
      positionProgramList: getItem('program_results'),
      session_uid: (window.session_uid || window.sessionStorage.getItem('session_uid')),
      ttclid: Cookies.get('ttclid'),
      ttp: Cookies.get('_ttp'),
      rdclid: window.localStorage.getItem('rdclid'),
      qclid: window.localStorage.getItem('qclid'),
      msclkid: window.localStorage.getItem('msclkid'),
      ...params,
    }

    // if (!data.session_uid) {
    //   data.session_uid = 'b-' + (new Date()).getTime() + Math.floor(Math.random() * 1000)
    //   window.session_uid = data.session_uid
    //   setSession({lang: i18n.resolvedLanguage})
    // }

    dataFetch(
      process.env.REACT_APP_API_URI + 'web-members',
      data,
      ({uid}) => {
        // const response = {"uid":"02d6b0ff-6dae-44dc-9400-68673ef7dab8"}

        identify(uid, email, {lang: i18n.resolvedLanguage})

        if (callback) {
          callback(uid)
          setIsLoading(false)
        } else {
          setTrackStep('email page', {...step, email, subscribe}, toAdd)
          navigate(nextUrl)
        }

        if (onSubmit)
          onSubmit(uid)
      }
    )
  }

  function applySuggestion(suggestion) {
    setEmail(suggestion)
    setSuggestions([])
  }

  return {
    handleSubmit,
    isLoading,

    toValidate: (force = false) => {
      if (!validateEmail(email)) {
        if (force)
          inputRef.current?.focus()

        return false
      }

      return true
    },

    handleSubscribe: (toEmail) => {
      fetch(
        `${process.env.REACT_APP_API_URI}web-members/${toEmail ? 'subscribe' : 'unsubscribe'}/${window._uid || window.localStorage.getItem('uid')}`
      ).then(() => {})

      setTrack(toEmail ? 'Subscribe' : 'Unsubscribe', {template: null})
    },

    input:
      <>
        <input ref={inputRef} id={'email'} type={'email'} placeholder={t('email')} value={email}
               className={'form-control'} required autoComplete={'email'}
               onInput={handleChange}/>

        {suggestions.length > 0 && (
          <ul className="form-control suggestions text-start">
            {suggestions.map((s, index) => (
              <li key={index} onClick={() => applySuggestion(s)}>{s}</li>
            ))}
          </ul>
        )}
      </>
  }
}